import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/anime';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { to: '/', label: t('首页') },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`font-heading text-2xl whitespace-nowrap transition-colors ${
              scrolled || !isHome ? 'text-foreground-900' : 'text-white'
            }`}
          >
            {t('网站名称')}
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                  scrolled || !isHome ? 'text-foreground-700' : 'text-white/90 hover:text-white'
                } ${location.pathname === link.to ? 'text-primary-500' : ''}`}
              >
                {link.label}
              </Link>
            ))}

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setCategoryOpen(!categoryOpen)}
                className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 cursor-pointer ${
                  scrolled || !isHome ? 'text-foreground-700' : 'text-white/90 hover:text-white'
                } ${location.pathname.startsWith('/category') ? 'text-primary-500' : ''}`}
              >
                {t('分类')}
                <i className={`${categoryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs transition-transform`}></i>
              </button>

              {categoryOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 z-50">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                        location.pathname === `/category/${cat.slug}`
                          ? 'text-primary-500 bg-primary-50'
                          : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      <span className="flex-1">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.count}部</span>
                    </Link>
                  ))}
                  <div className="border-t border-background-200/70 mt-1 pt-1">
                    <Link
                      to="/category/action"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-primary-500 hover:bg-primary-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-apps-2-line text-base w-5 h-5 flex items-center justify-center"></i>
                      <span>{t('查看全部分类')}</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium whitespace-nowrap transition-colors hover:text-primary-500 ${
                scrolled || !isHome ? 'text-foreground-700' : 'text-white/90 hover:text-white'
              } ${location.pathname === '/about' ? 'text-primary-500' : ''}`}
            >
              {t('关于我们')}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-4">
            <button
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors ${
                scrolled || !isHome
                  ? 'text-foreground-600 hover:text-primary-500 hover:bg-background-200'
                  : 'text-white/80 hover:text-white hover:bg-white/10'
              }`}
            >
              <i className="ri-search-line text-lg"></i>
            </button>
            <Link
              to="/about"
              className={`whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full border transition-all ${
                scrolled || !isHome
                  ? 'border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white'
                  : 'border-white/60 text-white hover:bg-white hover:text-foreground-900'
              }`}
            >
              {t('登录')}
            </Link>
          </div>

          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-full ${
              scrolled || !isHome ? 'text-foreground-700' : 'text-white'
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className={`text-sm font-medium py-2.5 px-3 rounded-lg transition-colors ${
                location.pathname === '/' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('首页')}
            </Link>

            <div>
              <p className="text-xs text-foreground-400 py-1.5 px-3 font-medium">{t('分类')}</p>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-2.5 text-sm py-2.5 px-3 rounded-lg transition-colors ${
                    location.pathname === `/category/${cat.slug}`
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                  <span>{cat.name}</span>
                  <span className="text-xs text-foreground-400 ml-auto">{cat.count}部</span>
                </Link>
              ))}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium py-2.5 px-3 rounded-lg transition-colors ${
                location.pathname === '/about' ? 'text-primary-500 bg-primary-50' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              {t('关于我们')}
            </Link>

            <div className="flex items-center gap-3 pt-2 border-t border-background-200/70">
              <button className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-200">
                <i className="ri-search-line text-lg"></i>
              </button>
              <Link
                to="/about"
                className="whitespace-nowrap text-sm font-medium px-4 py-2 rounded-full border border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white transition-all"
              >
                {t('登录')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}